import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import BibliothequePlotly from '../BibliothequePlotly/BibliothequePlotly';
import { convertiUtsToDate } from '../GrapheXT/GrapheXT';
import './LabelEventGraphDemo.css';
import * as glossaire_type_de_graphe from '../../../../ressources/glossaires/glossaire_type_de_graphe';
import {
  getLabels,
  createLabel,
  updateLabel,
  deleteLabel,
  getPhases,
  createPhase,
  updatePhase,
  deletePhase,
} from '../../../../services/api';
import {
  LABEL_TYPES,
  GeoIcon,
  COULEURS_PHASES,
  LabelHoverCard,
  LabelEditDialog,
  PhaseEditDialog,
} from './LabelComponents';
import Bouton from '../../../UI/Bouton/Bouton';
import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';

const EVENT_STYLES = {
  circle: { color: '#0077b6', symbol: 'circle' },
  diamond: { color: '#e07b00', symbol: 'diamond' },
};
export const DEFAULT_EVENT_STYLE = { color: '#888888', symbol: 'circle' };

const SIGNAL_DOMAIN = [0, 0.78];
const LABEL_DOMAIN = [0.84, 1.0];
const TOOLBAR_H = 46;
const SECTION_ALPHA = '28';

// ─── Helpers module-level ─────────────────────────────────────────────────────

const mapApiLabel = (l) => ({
  id: l.id,
  pk: l.pk_test_ref ?? null,
  t: l.time_unix ?? l.t,
  label: l.label_text ?? l.label,
  note: l.note || '',
  labelType: l.source === 'IA' ? 'IA' : 'JALON',
  iconType: l.source === 'IA' ? 'circle' : 'diamond',
  precision: l.score ? parseFloat(l.score) : null,
  urlSon: l.audio_path ?? null,
});

const mapApiSection = (s) => ({
  id: s.id,
  idSensor: s.id_sensor,
  pkStart: s.pk_test_ref_start,
  pkEnd: s.pk_test_ref_end,
  tStart: s.time_start ?? s.t_start,
  tEnd: s.time_end ?? s.t_end,
  label: s.phase_text ?? s.label,
  color: s.color_hex ?? s.color,
});

const parseSensorPositionFormula = (formula) => {
  const [sensorId, position] = formula
    .split(';')[0]
    .match(/\d+/g)
    .map(Number);
  return { sensorId, position };
};

const mapDraftToApiPayload = (draft) => {
  const { sensorId, position } = parseSensorPositionFormula(
    draft.sensorAndPosition
  );
  return {
    id_sensor: sensorId,
    position_in_sensor: position,
    pk_test_ref: draft.pk ?? null,
    time_unix: draft.t,
    label_text: draft.label ?? '',
  };
};

const buildAnnotation = ({
  xDate,
  paperY,
  label,
  dateStr,
  note,
  color,
  valLine = '',
}) => ({
  x: xDate,
  xref: 'x',
  y: paperY,
  yref: 'paper',
  text:
    `<b style="font-size:13px">${label}</b>` +
    `<br><span style="color:#555">Date :</span>  ${dateStr}` +
    valLine +
    (note ? `<br><span style="color:#555">Note :</span>  ${note}` : ''),
  showarrow: true,
  arrowhead: 2,
  arrowsize: 0.8,
  arrowwidth: 2,
  arrowcolor: color,
  ax: 0,
  ay: 36,
  xanchor: 'center',
  yanchor: 'top',
  cliponaxis: false,
  align: 'left',
  font: { size: 12, color: '#1a1a2e', family: 'Montserrat, sans-serif' },
  bgcolor: 'rgba(255,255,255,0.97)',
  bordercolor: color,
  borderwidth: 1.5,
  borderpad: 10,
});

const buildEventTraces = ({
  items,
  tRaw,
  yRaw,
  yMax,
  tTotal,
  domain,
  yaxisKey,
  markerSize,
  shownLabels,
}) => {
  const sorted = [...items]
    .map((ev, i) => ({ ...ev, _i: i }))
    .sort((a, b) => a.t - b.t);

  const PROXIMITY = tTotal * 0.08;
  let groupId = 0;
  const groups = sorted.map((ev, i) => {
    if (i > 0 && Math.abs(ev.t - sorted[i - 1].t) > PROXIMITY) groupId++;
    return groupId;
  });

  const groupCount = {};
  const indexAtX = {};
  const traces = [];
  const annMeta = [];

  sorted.forEach((ev, i) => {
    const iconType = ev.iconType || ev.label;
    const style = EVENT_STYLES[iconType] || DEFAULT_EVENT_STYLE;
    const xDate = convertiUtsToDate([ev.t])[0];
    const key = ev.t;
    indexAtX[key] = indexAtX[key] || 0;
    const stackIdx = indexAtX[key];
    indexAtX[key]++;
    const markerY = 0.5 + ((stackIdx % 3) - Math.min(stackIdx, 2) / 2) * 0.3;

    let yCurve = yMax;
    if (tRaw.length > 0) {
      let closest = 0,
        minDiff = Math.abs(tRaw[0] - ev.t);
      for (let j = 1; j < tRaw.length; j++) {
        const diff = Math.abs(tRaw[j] - ev.t);
        if (diff < minDiff) {
          minDiff = diff;
          closest = j;
        }
      }
      yCurve = yRaw[closest] ?? yMax;
    }

    const gid = groups[i];
    groupCount[gid] = (groupCount[gid] || 0) + 1;

    const displayLabel = ev.label || iconType;
    const showLegend =
      !shownLabels.has(iconType) &&
      (() => {
        shownLabels.add(iconType);
        return true;
      })();

    traces.push({
      type: 'scatter',
      mode: 'markers',
      name: displayLabel,
      legendgroup: iconType,
      showlegend: showLegend,
      xaxis: 'x',
      yaxis: yaxisKey,
      x: [xDate],
      y: [markerY],
      cliponaxis: false,
      hoverinfo: 'none',
      customdata: [ev.t],
      marker: {
        size: markerSize,
        color: style.color,
        symbol: style.symbol,
        line: { color: style.color, width: 2.5 },
      },
    });

    const dateStr = new Date(ev.t * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const valLine = `<br><span style="color:#555">Valeur :</span>  <b>${Math.round(yCurve * 10) / 10}</b>`;

    annMeta.push({
      xDate,
      markerY,
      style,
      label: displayLabel,
      note: ev.note,
      dateStr,
      yCurveVal: yCurve,
      valLine,
      domain,
    });
  });

  return { traces, annMeta };
};

// ─── Composant ────────────────────────────────────────────────────────────────

const LabelEventGraph = ({
  donnees = [],
  configuration = {},
  donneesGraphe,
  dimensionWidth,
  dimensionHeight,
  switchAfficherModifierIntervalle,
  viewobj_json_content = { displayModeBar: true },
  onLabelHover,
  onLabelUnhover,
  onPhaseClick,
  onAddLabel,
}) => {
  const isLabelMode =
    donneesGraphe?.config?.graph_type_id ===
    glossaire_type_de_graphe.graphe_x_t_label;

  const graphId = donneesGraphe?.config?.graph_id;

  // graphCurves : depuis donneesGraphe si disponible, sinon dérivé de donnees
  const graphCurves = useMemo(() => {
    if (donneesGraphe?.graphCurves?.length) return donneesGraphe.graphCurves;
    return donnees
      .filter((d) => d.id_sensor && d.position_in_sensor !== undefined)
      .map((d) => ({ formula: `#${d.id_sensor},${d.position_in_sensor}#` }));
  }, [donneesGraphe?.graphCurves, donnees]);

  // sensorIds : depuis donneesGraphe si disponible, sinon dérivé de donnees
  const sensorIds = useMemo(() => {
    if (donneesGraphe?.id_sensor?.length) return donneesGraphe.id_sensor;
    return [...new Set(donnees.map((d) => d.id_sensor).filter(Boolean))];
  }, [donneesGraphe?.id_sensor, donnees]);

  // ── Refs ──────────────────────────────────────────────────────────────────────
  const sectionsRef = useRef([]);
  const tRawRef = useRef([]);
  const menuRef = useRef(null);
  const timerEtiquette = useRef(null);
  const prochainIdPhase = useRef(0);
  const signalHoverTimer = useRef(null);

  // ── State ─────────────────────────────────────────────────────────────────────
  const [localLabels, setLocalLabels] = useState([]);
  const [localPhases, setLocalPhases] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [signalHover, setSignalHover] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null);
  const [creatingLabel, setCreatingLabel] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [creatingPhase, setCreatingPhase] = useState(null);
  const [labelError, setLabelError] = useState(null);

  // ── Options de dates (pour les dialogues) ────────────────────────────────────
  const dateOptions = useMemo(() => {
    const tArr = donnees[0]?.x || donnees[0]?.t || [];
    const yArr = donnees[0]?.y || [];
    const pkArr = donnees[0]?.pk || [];
    return tArr
      .map((ts, i) => ({ ts, y: yArr[i], pk: pkArr[i] ?? i }))
      .filter((_, i) => i % 2 === 0);
  }, [donnees]);

  // ── Chargement labels + phases depuis l'API ───────────────────────────────────
  useEffect(() => {
    if (!isLabelMode || !graphId) return;

    setLocalLabels([]);
    setLocalPhases([]);

    getPhases({ graphId }).then((res) => {
      const phases = res?.data?.data;
      if (Array.isArray(phases)) {
        const mapped = phases.map(mapApiSection);
        prochainIdPhase.current = mapped.length;
        setLocalPhases(mapped);
      }
    });

    sensorIds.forEach((sensorId) => {
      getLabels({ sensorId }).then((res) => {
        const labels = res?.data?.data;
        if (Array.isArray(labels))
          setLocalLabels((prev) => [...prev, ...labels.map(mapApiLabel)]);
      });
    });
  }, [graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fermer le menu au clic extérieur ─────────────────────────────────────────
  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen]);

  // ── Cleanup timer ─────────────────────────────────────────────────────────────
  useEffect(
    () => () => {
      if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
      if (timerEtiquette.current) clearTimeout(timerEtiquette.current);
    },
    []
  );

  // ── Rappels — labels ──────────────────────────────────────────────────────────

  const addLabel = useCallback(
    (type = 'JALON') => {
      if (!dateOptions.length) return;
      const labelConfig = LABEL_TYPES[type];
      const midPoint = dateOptions[Math.floor(dateOptions.length / 2)];
      setCreatingLabel({
        id: null,
        t: midPoint.ts,
        pk: midPoint.pk,
        iconType: labelConfig.iconType,
        labelType: type,
        label: '',
        note: '',
        sensorAndPosition: graphCurves[0]?.formula || '',
      });
    },
    [dateOptions, graphCurves]
  );

  const handleAddLabelFromSignal = useCallback(
    ({ ts, yVal }) => {
      if (!dateOptions.length) return;
      const closest = dateOptions.reduce((prev, curr) =>
        Math.abs(curr.ts - ts) < Math.abs(prev.ts - ts) ? curr : prev
      );
      setCreatingLabel({
        id: null,
        t: closest.ts,
        pk: closest.pk,
        iconType: LABEL_TYPES.JALON.iconType,
        labelType: 'JALON',
        label: '',
        note:
          yVal !== undefined ? `Valeur : ${Math.round(yVal * 10) / 10}` : '',
        sensorAndPosition: graphCurves[0]?.formula || '',
      });
    },
    [dateOptions, graphCurves]
  );

  const handleSaveLabel = useCallback(
    async (draft) => {
      try {
        const payload = mapDraftToApiPayload(draft);
        let savedLabel;
        if (draft.id == null) {
          const res = await createLabel(payload);
          const raw = res.data?.data ?? res.data;
          savedLabel = (raw?.time_unix != null || raw?.t != null)
            ? raw
            : { id: raw?.id ?? draft.id, time_unix: draft.t, pk_test_ref: draft.pk,
                label_text: draft.label ?? '', source: draft.labelType === 'IA' ? 'IA' : 'MANUAL' };
        } else {
          const res = await updateLabel({ labelId: draft.id, ...payload });
          const raw = res.data?.data ?? res.data;
          savedLabel = (raw?.time_unix != null || raw?.t != null)
            ? raw
            : { id: raw?.id ?? draft.id, time_unix: draft.t, pk_test_ref: draft.pk,
                label_text: draft.label ?? '', source: draft.labelType === 'IA' ? 'IA' : 'MANUAL' };
        }
        setLocalLabels((prev) =>
          draft.id == null
            ? [...prev, mapApiLabel(savedLabel)]
            : prev.map((l) =>
                l.id === savedLabel.id ? mapApiLabel(savedLabel) : l
              )
        );
        setEditingLabel(null);
        setCreatingLabel(null);
      } catch (err) {
        const detail = err?.response?.data?.detail ?? '';
        if (detail.includes('uniq_sensor_position_time') || detail.includes('Duplicate entry')) {
          setLabelError('Un label existe déjà pour ce capteur à cette date et position.');
        } else {
          setLabelError('Erreur lors de la sauvegarde. Veuillez réessayer.');
        }
      }
    },
    []
  );

  const handleRemoveLabel = useCallback(async () => {
    if (!hoveredLabel) return;
    const { id } = hoveredLabel.label;
    const res = await deleteLabel({ labelId: id });
    if (res.ok) setLocalLabels((prev) => prev.filter((l) => l.id !== id));
    setHoveredLabel(null);
  }, [hoveredLabel]);

  // ── Rappels — phases ──────────────────────────────────────────────────────────

  const ajouterPhase = useCallback(() => {
    const color =
      COULEURS_PHASES[prochainIdPhase.current % COULEURS_PHASES.length];
    const tArr = dateOptions.map((d) => d.ts);
    setCreatingPhase({
      id: null,
      tStart: tArr[0] || 0,
      tEnd: tArr[tArr.length - 1] || 0,
      label: '',
      color,
    });
  }, [dateOptions]);

  const handleSavePhase = useCallback(
    async (draft) => {
      const tArr = donnees[0]?.x || donnees[0]?.t || [];
      const pkArr = donnees[0]?.pk || [];
      const findPk = (ts) => {
        let closest = 0, minDiff = Infinity;
        tArr.forEach((t, i) => { const d = Math.abs(t - ts); if (d < minDiff) { minDiff = d; closest = i; } });
        return pkArr[closest] ?? closest;
      };
      const payload = {
        graph_id: graphId,
        id_sensor: sensorIds[0] ?? null,
        pk_test_ref_start: findPk(draft.tStart),
        pk_test_ref_end: findPk(draft.tEnd),
        time_start: draft.tStart,
        time_end: draft.tEnd,
        phase_text: draft.label ?? '',
        color_hex: draft.color ?? '#888888',
      };
      const res =
        draft.id == null
          ? await createPhase(payload)
          : await updatePhase({ phaseId: draft.id, ...payload });
      const raw = res?.data?.data ?? res?.data;
      const saved = (raw?.time_start != null || raw?.t_start != null)
        ? raw
        : {
            id: raw?.id ?? draft.id,
            id_sensor: sensorIds[0] ?? null,
            time_start: draft.tStart,
            time_end: draft.tEnd,
            phase_text: draft.label ?? '',
            color_hex: draft.color ?? '#888888',
          };
      if (saved) {
        const mapped = mapApiSection(saved);
        setLocalPhases((prev) =>
          draft.id == null
            ? [...prev, mapped]
            : prev.map((p) => (p.id === draft.id ? mapped : p))
        );
        prochainIdPhase.current += draft.id == null ? 1 : 0;
      }
      setCreatingPhase(null);
      setEditingPhase(null);
    },
    [graphId, sensorIds, donnees]
  );

  const handleDeletePhase = useCallback(async (draft) => {
    const res = await deletePhase({ phaseId: draft.id });
    if (res.ok)
      setLocalPhases((prev) => prev.filter((s) => s.id !== draft.id));
    setEditingPhase(null);
  }, []);

  const handlePhaseClickInternal = useCallback(
    (phaseData) => {
      const found = localPhases.find(
        (s) => s.tStart === phaseData.tStart && s.tEnd === phaseData.tEnd
      );
      if (found) setEditingPhase({ ...found });
    },
    [localPhases]
  );

  // ── Rappels survol — labels ───────────────────────────────────────────────────

  const handleLabelHoverInternal = useCallback(
    (t, x, y) => {
      const found = localLabels.find((l) => l.t === t);
      if (found) setHoveredLabel({ label: found, x, y });
    },
    [localLabels]
  );

  const handleLabelUnhoverInternal = useCallback(() => {
    timerEtiquette.current = setTimeout(() => setHoveredLabel(null), 150);
  }, []);

  const handlePencilEdit = useCallback(() => {
    if (hoveredLabel) setEditingLabel({ ...hoveredLabel.label });
  }, [hoveredLabel]);

  // ── useMemo traces + layout ───────────────────────────────────────────────────

  const { allTraces, annotations, layout } = useMemo(() => {
    const signalTraces = donnees.map((curve, curveIndex) => {
      const tRaw = curve.t || curve.x || [];
      if (curveIndex === 0) tRawRef.current = tRaw;
      return {
        ...curve,
        type: 'scatter',
        mode: 'lines',
        x: convertiUtsToDate(tRaw),
        y: curve.y || [],
        yaxis: 'y',
        name: curve.name || curve.sensor_name || `Signal ${curveIndex + 1}`,
        hovertemplate: '%{x}<br>%{y}<extra></extra>',
        // curve.color is the API-level color; ensure it reaches line.color
        line: { ...(curve.line || {}), color: curve.line?.color ?? curve.color },
        marker: { color: curve.color },
      };
    });

    const allYValues = donnees.flatMap((d) => d.y || []);
    const yMax = allYValues.length ? Math.max(...allYValues) : 1;
    const yMin = allYValues.length ? Math.min(...allYValues) : 0;
    const yRange = yMax - yMin || 1;

    const tBase = donnees[0]?.x || donnees[0]?.t || [];
    const tTotal = tBase.length > 1 ? tBase[tBase.length - 1] - tBase[0] : 1;
    const markerSize = configuration.markerSize || 16;
    const shownLabels = new Set();

    // Labels : config (statiques) + localLabels (API)
    const labelsRaw = [
      ...(Array.isArray(configuration.labels)
        ? configuration.labels
        : Array.isArray(configuration.events)
          ? configuration.events
          : []),
      ...localLabels,
    ].filter((l) => l.t != null && Number.isFinite(Number(l.t)));

    const { traces: labelTraces, annMeta: labelAnnMeta } = buildEventTraces({
      items: labelsRaw,
      tRaw: tBase,
      yRaw: donnees[0]?.y || [],
      yMax,
      tTotal,
      domain: LABEL_DOMAIN,
      yaxisKey: 'y2',
      markerSize,
      shownLabels,
    });

    // Sections : config (statiques) + localPhases (API)
    const sectionsRaw = [
      ...(Array.isArray(configuration.sections) ? configuration.sections : []),
      ...localPhases,
    ].filter(
      (s) =>
        s.tStart != null && Number.isFinite(Number(s.tStart)) &&
        s.tEnd   != null && Number.isFinite(Number(s.tEnd))
    );
    sectionsRef.current = sectionsRaw;

    const sectionShapes = sectionsRaw.map((sec) => {
      const x0 = convertiUtsToDate([sec.tStart])[0];
      const x1 = convertiUtsToDate([sec.tEnd])[0];
      const color = sec.color || '#888888';
      return {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0,
        x1,
        y0: SIGNAL_DOMAIN[0],
        y1: SIGNAL_DOMAIN[1],
        fillcolor: color + SECTION_ALPHA,
        line: { width: 0 },
        layer: 'below',
      };
    });

    const sectionAnnotations = sectionsRaw.map((sec) => {
      const xStart = convertiUtsToDate([sec.tStart])[0];
      const color = sec.color || '#888888';
      const title = sec.label
        ? `<b>${sec.label}</b>`
        : '<i style="color:#aaa">Phase</i>';
      return {
        x: xStart,
        xref: 'x',
        xanchor: 'left',
        y: SIGNAL_DOMAIN[1],
        yref: 'paper',
        yanchor: 'top',
        text: `${title} <span style="font-size:10px">✏</span>`,
        showarrow: false,
        font: { size: 11, color, family: 'Montserrat, sans-serif' },
        bgcolor: 'rgba(255,255,255,0.88)',
        bordercolor: color,
        borderwidth: 1,
        borderpad: 4,
        captureevents: true,
      };
    });

    const labelLines = labelAnnMeta.map((ev) => {
      const yNorm = Math.max(0, Math.min(1, (ev.yCurveVal - yMin) / yRange));
      const y0 =
        SIGNAL_DOMAIN[0] + (SIGNAL_DOMAIN[1] - SIGNAL_DOMAIN[0]) * yNorm;
      const y1 =
        LABEL_DOMAIN[0] + (LABEL_DOMAIN[1] - LABEL_DOMAIN[0]) * ev.markerY;
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: ev.xDate,
        x1: ev.xDate,
        y0,
        y1,
        line: { color: ev.style.color, width: 1.5, dash: 'dot' },
      };
    });

    const bandShapes = [
      {
        type: 'rect',
        xref: 'paper',
        yref: 'paper',
        x0: 0,
        x1: 1,
        y0: LABEL_DOMAIN[0] - 0.01,
        y1: LABEL_DOMAIN[1],
        fillcolor: 'rgba(240,243,255,0.6)',
        line: { width: 1, color: '#d0d5ee' },
        layer: 'below',
      },
    ];

    const shapes = [...sectionShapes, ...bandShapes, ...labelLines];

    const annotations = labelAnnMeta.map((ev) => {
      const paperY =
        LABEL_DOMAIN[0] + (LABEL_DOMAIN[1] - LABEL_DOMAIN[0]) * ev.markerY;
      return buildAnnotation({
        xDate: ev.xDate,
        paperY,
        label: ev.label,
        dateStr: ev.dateStr,
        note: ev.note,
        color: ev.style.color,
        valLine: ev.valLine,
      });
    });

    const layout = {
      title: {
        text: `${configuration.title || ''}${configuration.subtitle ? '<br>' + configuration.subtitle : ''}`,
      },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      xaxis: {
        title: { text: configuration.xaxis?.title || 't' },
        type: 'date',
        domain: [0, 1],
        showgrid: true,
        zeroline: false,
        anchor: 'y',
      },
      yaxis: {
        title: { text: configuration.yaxis?.title || '' },
        domain: SIGNAL_DOMAIN,
        range: [yMin - yRange * 0.05, yMax + yRange * 0.08],
        showgrid: true,
        zeroline: false,
      },
      yaxis2: {
        domain: LABEL_DOMAIN,
        range: [0, 1],
        fixedrange: true,
        visible: false,
        anchor: 'x',
      },
      shapes,
      annotations: sectionAnnotations,
      legend: {
        orientation: 'v',
        x: 1.02,
        y: 1,
        xanchor: 'left',
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.9)',
        bordercolor: '#ddd',
        borderwidth: 1,
        tracegroupgap: 8,
        font: { size: 12 },
      },
      margin: { l: 80, r: 230, t: 60, b: 60 },
    };

    const N_HOVER = 20;
    const yHover = yMax + yRange * 0.04;

    const phaseHoverTraces = sectionsRaw.map((sec) => {
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart,
        tEnd: sec.tEnd,
        label: sec.label,
        color: sec.color,
      };
      const xPoints = Array.from({ length: N_HOVER }, (_, i) => {
        const t = sec.tStart + ((sec.tEnd - sec.tStart) * i) / (N_HOVER - 1);
        return convertiUtsToDate([Math.round(t)])[0];
      });
      return {
        type: 'scatter',
        mode: 'markers',
        hoverinfo: 'none',
        showlegend: false,
        name: '',
        xaxis: 'x',
        yaxis: 'y',
        x: xPoints,
        y: Array(N_HOVER).fill(yHover),
        marker: {
          size: 18,
          color: 'rgba(0,0,0,0)',
          symbol: 'square',
          line: { width: 0 },
        },
        customdata: Array(N_HOVER).fill(phaseData),
      };
    });

    const N_LABEL_HOVER = 6;
    const labelHoverTraces = labelsRaw.map((ev) => {
      const xDate = convertiUtsToDate([ev.t])[0];
      return {
        type: 'scatter',
        mode: 'markers',
        hoverinfo: 'none',
        showlegend: false,
        name: '',
        xaxis: 'x',
        yaxis: 'y2',
        x: Array(N_LABEL_HOVER).fill(xDate),
        y: Array.from(
          { length: N_LABEL_HOVER },
          (_, i) => i / (N_LABEL_HOVER - 1)
        ),
        marker: {
          size: 22,
          color: 'rgba(0,0,0,0)',
          symbol: 'square',
          line: { width: 0 },
        },
        customdata: Array(N_LABEL_HOVER).fill(ev.t),
      };
    });

    return {
      allTraces: [
        ...signalTraces,
        ...labelTraces,
        ...phaseHoverTraces,
        ...labelHoverTraces,
      ],
      annotations,
      layout,
    };
  }, [donnees, configuration, localLabels, localPhases]);

  // ── Layout actif (hover annotation) ──────────────────────────────────────────

  const activeLayout = useMemo(() => {
    if (hoveredIndex === null) return layout;
    const ann = annotations[hoveredIndex];
    return {
      ...layout,
      annotations: [...(layout.annotations || []), ...(ann ? [ann] : [])],
    };
  }, [layout, annotations, hoveredIndex]);

  // ── Handlers Plotly ───────────────────────────────────────────────────────────

  const handleHover = useCallback(
    (e) => {
      const pt = e.points?.[0];
      if (!pt) return;

      if (pt.curveNumber === 0) {
        if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
        const ts = tRawRef.current[pt.pointNumber];
        setSignalHover({
          ts,
          yVal: pt.y,
          pointNumber: pt.pointNumber,
          clientX: e.event?.clientX,
          clientY: e.event?.clientY,
        });
        return;
      }

      setSignalHover(null);

      if (pt.customdata !== undefined) {
        if (pt.customdata?.type === 'phase') return;
        // Label hover
        if (isLabelMode) {
          handleLabelHoverInternal(
            pt.customdata,
            e.event?.clientX,
            e.event?.clientY
          );
        } else if (onLabelHover) {
          onLabelHover(pt.customdata, e.event?.clientX, e.event?.clientY);
        }
        return;
      }

      setHoveredIndex(pt.curveNumber - 1);
    },
    [isLabelMode, handleLabelHoverInternal, onLabelHover]
  );

  const handleUnhover = useCallback(() => {
    setHoveredIndex(null);
    signalHoverTimer.current = setTimeout(() => setSignalHover(null), 300);
    if (isLabelMode) {
      handleLabelUnhoverInternal();
    } else if (onLabelUnhover) {
      onLabelUnhover();
    }
  }, [isLabelMode, handleLabelUnhoverInternal, onLabelUnhover]);

  const handleClickAnnotation = useCallback(
    (e) => {
      if (e.index === undefined) return;
      const sec = sectionsRef.current[e.index];
      if (!sec) return;
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart,
        tEnd: sec.tEnd,
        label: sec.label,
        color: sec.color,
      };
      if (isLabelMode) {
        handlePhaseClickInternal(phaseData);
      } else if (onPhaseClick) {
        onPhaseClick(phaseData, e.event?.clientX, e.event?.clientY);
      }
    },
    [isLabelMode, handlePhaseClickInternal, onPhaseClick]
  );

  const handleClick = useCallback(
    (e) => {
      const pt = e.points?.[0];
      if (!pt || pt.customdata?.type !== 'phase') return;
      if (isLabelMode) {
        handlePhaseClickInternal(pt.customdata);
      } else if (onPhaseClick) {
        onPhaseClick(pt.customdata, e.event?.clientX, e.event?.clientY);
      }
    },
    [isLabelMode, handlePhaseClickInternal, onPhaseClick]
  );

  // ── Signal hover popup ────────────────────────────────────────────────────────

  const signalHoverPopup = signalHover && (
    <div
      className="leg-signal-popup"
      style={{
        left: signalHover.clientX + 14,
        top: signalHover.clientY - 18,
      }}
      onMouseEnter={() => {
        if (signalHoverTimer.current) clearTimeout(signalHoverTimer.current);
      }}
      onMouseLeave={() => {
        signalHoverTimer.current = setTimeout(() => setSignalHover(null), 300);
      }}
    >
      <div className="leg-signal-row">
        <span className="leg-signal-meta">Date</span>
        <span className="leg-signal-val">
          {signalHover.ts
            ? new Date(signalHover.ts * 1000).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '—'}
        </span>
      </div>
      <div className="leg-signal-row">
        <span className="leg-signal-meta">Valeur</span>
        <span className="leg-signal-val leg-signal-val--bold">
          {signalHover.yVal !== undefined
            ? `${Math.round(signalHover.yVal * 10) / 10}`
            : '—'}
        </span>
      </div>
      {(isLabelMode || onAddLabel) && (
        <button
          className="leg-btn-add-signal"
          onClick={() => {
            if (isLabelMode) {
              handleAddLabelFromSignal({
                ts: signalHover.ts,
                yVal: signalHover.yVal,
              });
            } else if (onAddLabel) {
              onAddLabel({ ts: signalHover.ts, yVal: signalHover.yVal });
            }
            setSignalHover(null);
          }}
        >
          + Add label
        </button>
      )}
    </div>
  );

  // ── Rendu ─────────────────────────────────────────────────────────────────────

  // In label mode the toolbar row takes TOOLBAR_H px — subtract so the Plotly
  // chart doesn't overflow its container (same total height as other graph types).
  // When dimensionHeight is undefined, pass undefined so BibliothequePlotly uses
  // its own window-based fallback (window.innerHeight - 165) instead of a fixed 400px.
  const plotHeight =
    isLabelMode && dimensionHeight !== undefined
      ? Math.max(200, dimensionHeight - TOOLBAR_H)
      : dimensionHeight;

  const plotly = (
    <BibliothequePlotly
      data={allTraces}
      layout={activeLayout}
      donneesGraphe={donneesGraphe}
      dimensionWidth={dimensionWidth}
      dimensionHeight={plotHeight}
      viewobj_json_content={viewobj_json_content}
      onClick={handleClick}
      onHover={handleHover}
      onUnhover={handleUnhover}
      onClickAnnotation={handleClickAnnotation}
      ouvrirPopupModifierIntervalle={switchAfficherModifierIntervalle}
    />
  );

  if (!isLabelMode) {
    return (
      <>
        {plotly}
        {signalHoverPopup}
      </>
    );
  }

  return (
    <div>
      {/* Toolbar row — exactly TOOLBAR_H px tall so plotHeight + TOOLBAR_H = dimensionHeight */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          height: TOOLBAR_H,
          paddingRight: 8,
        }}
      >
        <div ref={menuRef} style={{ position: 'relative' }}>
          <Bouton
            type={type_btn.succes}
            cliquer={() => setMenuOpen((o) => !o)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '6px 14px 6px 10px',
              height: '34px',
              borderRadius: '6px',
              flexShrink: 0,
            }}
            title="Ajouter"
          >
            <span className="leg-demo-btn-label">+ Ajouter</span>
          </Bouton>
          {menuOpen && (
            <div className="leg-dropdown-menu">
              <button
                className="leg-dropdown-item"
                onClick={() => {
                  addLabel('JALON');
                  setMenuOpen(false);
                }}
              >
                <GeoIcon iconType="diamond" size={12} />
                <span>Jalon</span>
              </button>
              <div className="leg-dropdown-divider" />
              <button
                className="leg-dropdown-item"
                onClick={() => {
                  ajouterPhase();
                  setMenuOpen(false);
                }}
              >
                <span>▬</span>
                <span>Phase</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {plotly}
      {signalHoverPopup}

      {hoveredLabel && !editingLabel && (
        <LabelHoverCard
          labelData={hoveredLabel.label}
          x={hoveredLabel.x}
          y={hoveredLabel.y}
          onEdit={handlePencilEdit}
          onRemove={handleRemoveLabel}
          onMouseEnter={() => clearTimeout(timerEtiquette.current)}
          onMouseLeave={() => setHoveredLabel(null)}
        />
      )}

      {editingLabel && (
        <LabelEditDialog
          label={editingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={donnees}
          error={labelError}
          onSave={handleSaveLabel}
          onCancel={() => { setEditingLabel(null); setLabelError(null); }}
        />
      )}

      {creatingLabel && (
        <LabelEditDialog
          label={creatingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={donnees}
          error={labelError}
          onSave={handleSaveLabel}
          onCancel={() => { setCreatingLabel(null); setLabelError(null); }}
        />
      )}

      {editingPhase && (
        <PhaseEditDialog
          phase={editingPhase}
          onSave={handleSavePhase}
          onCancel={() => setEditingPhase(null)}
          onDelete={handleDeletePhase}
        />
      )}

      {creatingPhase && (
        <PhaseEditDialog
          phase={creatingPhase}
          onSave={handleSavePhase}
          onCancel={() => setCreatingPhase(null)}
        />
      )}
    </div>
  );
};

export default LabelEventGraph;
